import { z } from "zod";
import { folderIdSchema } from "./folders.ts";
import { projectIdSchema } from "./projects.ts";
import { userIdSchema } from "./tenancy.ts";

/**
 * Project knowledge (ADR 0006, ADR 0009): documents, who may read them, where a passage sits
 * in its document, and the flow label every retrieved passage carries.
 */
export const documentIdSchema = z.string().brand<"DocumentId">();
export type DocumentId = z.infer<typeof documentIdSchema>;

/**
 * `uploading` until the upload is confirmed, then `processing` → `ready` | `failed`. A deleted
 * document is a tombstone (`deletedAt` set, title removed, readers kept) so labels and
 * citations that reference it stay checkable and render as "source deleted".
 */
export const documentStatusSchema = z.enum(["uploading", "processing", "ready", "failed"]);
export type DocumentStatus = z.infer<typeof documentStatusSchema>;

/**
 * An origin a client may set: `external` (supplied by a third party) sets the trust of everything
 * derived from it. `generated` is never chosen by a client (see {@link storedDocumentOriginSchema}).
 */
export const documentOriginSchema = z.enum(["internal", "external"]);

/**
 * An origin as stored and read back: also `generated`, set by the server only on documents
 * written by the agent (an artefact added to knowledge, or an answer saved from generated
 * sources; openspec change `artefacts-to-knowledge`). Clients treat an origin they do not know as
 * at least `internal`.
 */
export const storedDocumentOriginSchema = z.enum(["internal", "external", "generated"]);
export type StoredDocumentOrigin = z.infer<typeof storedDocumentOriginSchema>;

/**
 * `file`: uploaded; `saved_answer`: saved from a message; `wiki_page`: a published wiki page;
 * `attachment`: working material of one conversation (openspec change `conversation-attachments`),
 * never part of a project's knowledge; `artefact`: a conversation artefact version added to
 * knowledge (`artefacts-to-knowledge`).
 */
export const documentKindSchema = z.enum([
  "file",
  "saved_answer",
  "wiki_page",
  "attachment",
  "artefact",
]);
export type DocumentKind = z.infer<typeof documentKindSchema>;
export type DocumentOrigin = z.infer<typeof documentOriginSchema>;

/**
 * A document written by the agent (artefacts-to-knowledge): an artefact added to knowledge, or a
 * file of origin `generated`. Marked "Generated by agent" wherever it appears; never a primary
 * source.
 */
export function isGeneratedDocument(document: {
  kind?: string | undefined;
  origin?: string | undefined;
}): boolean {
  return document.kind === "artefact" || document.origin === "generated";
}

/**
 * A document whose origin the server derives from its sources (an added artefact, a saved answer,
 * a generated file): no client changes it (`422 origin_derived`).
 */
export function hasDerivedOrigin(document: { kind?: string; origin?: string }): boolean {
  return (
    document.kind === "artefact" ||
    document.kind === "saved_answer" ||
    document.origin === "generated"
  );
}

/**
 * The trust of a label (ADR 0009), ordered `internal < generated < external`: a union takes the
 * highest. `generated`: content the agent wrote was read back as a source. Computed by the
 * server only; no input carries it.
 */
export const trustSchema = z.enum(["internal", "generated", "external"]);
export type Trust = z.infer<typeof trustSchema>;

/** Trust as read back: the same three values (kept for the response schemas that name it). */
export const storedTrustSchema = trustSchema;
export type StoredTrust = Trust;

/**
 * Reader principal kinds. Open: code must treat an unknown kind as "grants nothing", so new
 * kinds arrive without changing callers.
 * - `project`: the project audience (`ref` is the project ID);
 * - `user`: one user (`ref` is the user ID);
 * - `entity`: members of one entity; `entity_tree`: of the entity and its descendants;
 * - `group`: members of a directory group, transitively through nested groups (`ref`: group ID);
 * - `attribute_rule`: users whose attributes match the rule right now (`ref`: rule ID).
 *
 * Stored only, never accepted from clients: `conversation` (`ref`: a conversation ID), the people
 * who can open that conversation right now, bounded by the document's readers floor
 * ({@link CONVERSATION_READER_KIND}).
 */
export const KNOWN_READER_KINDS = [
  "project",
  "user",
  "entity",
  "entity_tree",
  "group",
  "attribute_rule",
] as const;
export const readerKindSchema = z.enum(KNOWN_READER_KINDS);
export type ReaderKind = z.infer<typeof readerKindSchema>;

/**
 * The server-written reader kind of conversation attachments: deliberately not in
 * {@link KNOWN_READER_KINDS}, so {@link readerSchema} refuses it from any client.
 */
export const CONVERSATION_READER_KIND = "conversation";

/** A reader to write: only known kinds. */
export const readerSchema = z.object({ kind: readerKindSchema, ref: z.string().min(1) });
export type Reader = z.infer<typeof readerSchema>;

/** A reader as read back: any kind, so older clients keep parsing when new kinds arrive. */
export const storedReaderSchema = z.object({ kind: z.string(), ref: z.string() });
export type StoredReader = z.infer<typeof storedReaderSchema>;

/** A rectangle on a page or image, as fractions (0..1) of its width and height. */
export const regionSchema = z.object({
  left: z.number().min(0).max(1),
  top: z.number().min(0).max(1),
  width: z.number().min(0).max(1),
  height: z.number().min(0).max(1),
});
export type Region = z.infer<typeof regionSchema>;

/** Where a passage sits in its document; the shape depends on the format. */
export const passageLocationSchema = z.union([
  /** PDF; `sectionPath` from the outline or detected headings; `region` for OCR'd blocks. */
  z.object({
    page: z.int().min(1),
    sectionPath: z.array(z.string()).optional(),
    region: regionSchema.optional(),
  }),
  /** DOCX, Markdown, HTML, plain text. */
  z.object({ sectionPath: z.array(z.string()), ordinal: z.int().min(0) }),
  /** PPTX. */
  z.object({ slide: z.int().min(1) }),
  /** XLSX, CSV. */
  z.object({ sheet: z.string(), range: z.string() }),
  /** PNG, JPEG: the image, or a region of it. */
  z.object({ image: z.literal(true), region: regionSchema.optional() }),
]);
export type PassageLocation = z.infer<typeof passageLocationSchema>;

/**
 * How a passage's text was produced, when not read from the file's own text: `ocr` (transcribed
 * from a page or image by the `vision` role) or `caption` (a model's description of an image).
 * `flagged`: the transcription guard could not confirm part of it; readers should check the
 * original. Null (absent) for text read from the file.
 */
export const passageProvenanceSchema = z.object({
  source: z.enum(["ocr", "caption"]),
  flagged: z.boolean(),
});
export type PassageProvenance = z.infer<typeof passageProvenanceSchema>;

/** A reference to one version of a document; labels and citations store these, never URLs. */
export const documentRefSchema = z.object({
  documentId: documentIdSchema,
  version: z.int().min(1),
});
export type DocumentRef = z.infer<typeof documentRefSchema>;

/**
 * Flow label (ADR 0009): readers are resolved from the sources whenever it is checked. `pins`
 * (version-aware-labels): for each wiki page among the sources, every page version read; a
 * pinned page is also bounded by the readers and gating sources that version was published
 * with. Absent: no pins (every source follows the document-level rule alone).
 */
export const flowLabelSchema = z.object({
  sources: z.array(documentIdSchema),
  trust: trustSchema,
  pins: z.array(documentRefSchema).optional(),
});
export type FlowLabel = z.infer<typeof flowLabelSchema>;

/** Post-generation check of a citation; `unchecked` when the check could not run. */
export const citationStatusSchema = z.enum(["supported", "unsupported", "unchecked"]);
export type CitationStatus = z.infer<typeof citationStatusSchema>;

export const messageCitationSchema = z.object({
  /** Per-turn handle the model cites, e.g. `S3`. */
  handle: z.string(),
  documentId: documentIdSchema,
  version: z.int().min(1),
  location: passageLocationSchema,
  status: citationStatusSchema,
  /** Set when the cited passage was transcribed or captioned (show "from OCR"). */
  provenance: passageProvenanceSchema.nullable().optional(),
});
export type MessageCitation = z.infer<typeof messageCitationSchema>;

/** A readers floor: the widest audience a classification level allows. */
export const readersFloorSchema = z.enum(["project", "entity", "named"]);

/**
 * A level of the tenant classification scheme (`knowledge.classificationScheme`), ordered from
 * least to most sensitive. `readersFloor` is the widest reader set the level allows;
 * `egressFloor`, when set, is the floor the egress gate applies to the level instead.
 */
export const classificationLevelSchema = z.strictObject({
  id: z
    .string()
    .trim()
    .regex(/^[a-z][a-z0-9_-]{0,39}$/, "lowercase id"),
  label: z.string().trim().min(1).max(60),
  downloadAllowed: z.boolean(),
  readersFloor: readersFloorSchema,
  /** The floor outbound tools treat the level as (ADR 0011); its readers floor when absent. */
  egressFloor: readersFloorSchema.optional(),
});
export type ClassificationLevel = z.infer<typeof classificationLevelSchema>;

/**
 * Whether `egressFloor` is allowed with `readersFloor`: no stricter (`project` < `entity` <
 * `named`). An egress floor only loosens what outbound tools may take, so a level saved without
 * it (as the release before private-egress-floor's SPA does) never egresses wider than intended.
 */
export function egressFloorAllowed(
  readersFloor: ClassificationLevel["readersFloor"],
  egressFloor: ClassificationLevel["readersFloor"],
): boolean {
  const rank = readersFloorSchema.options;
  // Fail closed on a value outside the enum (callers parse first; this never answers "allowed" for one).
  const egress = rank.indexOf(egressFloor);
  return egress !== -1 && egress <= rank.indexOf(readersFloor);
}

/**
 * The ids of `after`'s levels whose egress floor (`egressFloor`, else `readersFloor`) is looser
 * than before: than the same id's in `before`, or than its own readers floor for a level `before`
 * lacks (#262). `named` keeps those whose readers floor is or was `named` only.
 */
export function loosenedEgressLevels(
  before: readonly ClassificationLevel[],
  after: readonly ClassificationLevel[],
  options: { named?: boolean } = {},
): string[] {
  const rank = (floor: ClassificationLevel["readersFloor"]) =>
    readersFloorSchema.options.indexOf(floor);
  const egress = (level: ClassificationLevel) => rank(level.egressFloor ?? level.readersFloor);
  const previous = new Map(before.map((l) => [l.id, l]));
  return after
    .filter((level) => {
      const old = previous.get(level.id);
      const named = level.readersFloor === "named" || old?.readersFloor === "named";
      return (
        (!options.named || named) && egress(level) < (old ? egress(old) : rank(level.readersFloor))
      );
    })
    .map((level) => level.id);
}

export const classificationSchemeSchema = z
  .array(classificationLevelSchema)
  .min(1)
  .refine((levels) => new Set(levels.map((l) => l.id)).size === levels.length, {
    message: "level ids must be unique",
  });
export type ClassificationScheme = z.infer<typeof classificationSchemeSchema>;

/** Instance default scheme; tenants replace it with their own levels. */
export const DEFAULT_CLASSIFICATION_SCHEME: ClassificationScheme = [
  { id: "public", label: "Public", downloadAllowed: true, readersFloor: "project" },
  { id: "internal", label: "Internal", downloadAllowed: true, readersFloor: "project" },
  // Narrower audience than Internal (the creator and the people they name), same outbound
  // tools: personal working material, such as a standalone conversation's files.
  {
    id: "private",
    label: "Private",
    downloadAllowed: true,
    readersFloor: "named",
    egressFloor: "project",
  },
  { id: "confidential", label: "Confidential", downloadAllowed: true, readersFloor: "entity" },
  { id: "restricted", label: "Restricted", downloadAllowed: false, readersFloor: "named" },
];

/**
 * The tenant's private level: the least sensitive level whose readers floor is `named` (the
 * creator and the people they name), or `undefined` when the scheme has none. Standalone
 * conversations upload their files at it (openspec change standalone-attachments-private, D1);
 * the backoffice's scheme editor words that role with the same rule.
 */
export function privateLevel(
  scheme: readonly ClassificationLevel[],
): ClassificationLevel | undefined {
  return scheme.find((l) => l.readersFloor === "named");
}

type Floor = ClassificationLevel["readersFloor"];
const floorRank = (floor: Floor) => readersFloorSchema.options.indexOf(floor);
const egressOf = (level: ClassificationLevel): Floor => level.egressFloor ?? level.readersFloor;

/** A level the scheme change touches, as the review before saving words it. */
interface LevelRef {
  id: string;
  /** Its name after the change (before it, for a removed level). */
  label: string;
}

/** What a scheme change does (openspec change classification-editor, design D5). */
export interface SchemeChanges {
  added: LevelRef[];
  renamed: (LevelRef & { from: string })[];
  /** Kept levels whose position relative to another kept level changed, in their new order. */
  moved: (LevelRef & { from: number; to: number })[];
  readersTightened: (LevelRef & { from: Floor; to: Floor })[];
  readersLoosened: (LevelRef & { from: Floor; to: Floor })[];
  downloadChanged: (LevelRef & { allowed: boolean })[];
  /** Effective egress floor (`egressFloor`, else readers) looser; as {@link loosenedEgressLevels}. */
  egressLoosened: (LevelRef & { from: Floor; to: Floor })[];
  egressTightened: (LevelRef & { from: Floor; to: Floor })[];
  removed: LevelRef[];
}

/**
 * Every change from `before` to `after`, per kind, each level at most once per kind. Levels are
 * matched by id (a rename keeps it). `from`/`to` of `moved` are 0-based positions in the full
 * schemes; an insert or a removal that keeps the kept levels' relative order moves nobody.
 */
export function schemeChanges(
  before: readonly ClassificationLevel[],
  after: readonly ClassificationLevel[],
): SchemeChanges {
  const previous = new Map(before.map((l, i) => [l.id, { level: l, index: i }]));
  const next = new Set(after.map((l) => l.id));
  const kept = after.filter((l) => previous.has(l.id));
  // Pairwise: a level moved when its order against any other kept level flipped (a reversal's
  // middle level keeps its index among the kept ones, yet both neighbours crossed it).
  const keptBefore = new Map(
    before.filter((l) => next.has(l.id)).map((l, i) => [l.id, i] as const),
  );
  const movedIds = new Set<string>();
  kept.forEach((a, i) => {
    for (const b of kept.slice(i + 1)) {
      if ((keptBefore.get(a.id) ?? 0) > (keptBefore.get(b.id) ?? 0)) {
        movedIds.add(a.id);
        movedIds.add(b.id);
      }
    }
  });
  const loosened = new Set(loosenedEgressLevels(before, after));
  const changes: SchemeChanges = {
    added: [],
    renamed: [],
    moved: [],
    readersTightened: [],
    readersLoosened: [],
    downloadChanged: [],
    egressLoosened: [],
    egressTightened: [],
    removed: [],
  };
  after.forEach((level, index) => {
    const ref = { id: level.id, label: level.label };
    const old = previous.get(level.id);
    if (!old) {
      changes.added.push(ref);
      if (loosened.has(level.id)) {
        changes.egressLoosened.push({ ...ref, from: level.readersFloor, to: egressOf(level) });
      }
      return;
    }
    const was = old.level;
    if (was.label !== level.label) {
      changes.renamed.push({ ...ref, from: was.label });
    }
    if (movedIds.has(level.id)) {
      changes.moved.push({ ...ref, from: old.index, to: index });
    }
    const readers = { ...ref, from: was.readersFloor, to: level.readersFloor };
    if (floorRank(level.readersFloor) > floorRank(was.readersFloor)) {
      changes.readersTightened.push(readers);
    } else if (floorRank(level.readersFloor) < floorRank(was.readersFloor)) {
      changes.readersLoosened.push(readers);
    }
    if (was.downloadAllowed !== level.downloadAllowed) {
      changes.downloadChanged.push({ ...ref, allowed: level.downloadAllowed });
    }
    const egress = { ...ref, from: egressOf(was), to: egressOf(level) };
    if (loosened.has(level.id)) {
      changes.egressLoosened.push(egress);
    } else if (floorRank(egress.to) > floorRank(egress.from)) {
      changes.egressTightened.push(egress);
    }
  });
  for (const level of before) {
    if (!next.has(level.id)) {
      changes.removed.push({ id: level.id, label: level.label });
    }
  }
  return changes;
}

/** A name as level names compare: case and accents ignored, outer spaces trimmed. */
export function foldLevelName(name: string): string {
  return name.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().trim();
}

const LEVEL_ID_MAX = 40;

/**
 * The id of a new level, derived once from its first name (design D4): accents folded,
 * lowercased, every run outside `[a-z0-9]` turned into `-`, prefixed `level-` when it would not
 * start with a letter (`level` for a name with no letter or digit), cut to 40 characters, then
 * suffixed `-2`, `-3`… while `taken` has it. Always valid for `classificationLevelSchema`.
 */
export function deriveLevelId(name: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  const trim = (s: string, max: number) => s.slice(0, max).replace(/-+$/, "");
  const slug = foldLevelName(name)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  const base = trim(
    slug === "" ? "level" : /^[a-z]/.test(slug) ? slug : `level-${slug}`,
    LEVEL_ID_MAX,
  );
  if (!used.has(base)) {
    return base;
  }
  for (let n = 2; ; n++) {
    const suffix = `-${n}`;
    const candidate = `${trim(base, LEVEL_ID_MAX - suffix.length)}${suffix}`;
    if (!used.has(candidate)) {
      return candidate;
    }
  }
}

/**
 * `GET /api/admin/classification/usage`: per level id, the tenant's non-deleted documents and
 * stored default classifications (tenant, entities, projects) that use it. Counts only.
 */
export const classificationUsageResponseSchema = z.object({
  levels: z.record(z.string(), z.object({ documents: z.int().min(0), defaults: z.int().min(0) })),
});
export type ClassificationUsageResponse = z.infer<typeof classificationUsageResponseSchema>;

/**
 * Where a document of kind `artefact` came from (design §5), for its current version, without
 * content. `conversation` (its ID, project and title) is set only for a viewer who can open that
 * conversation and passes its label; anyone else reads "From a conversation", and every viewer
 * reads "From a deleted conversation" once it is deleted. `model` is the display name of the
 * model that wrote the version, null when unknown.
 */
export const documentProvenanceSchema = z.object({
  conversation: z
    .object({
      id: z.string(),
      projectId: projectIdSchema.nullable(),
      title: z.string().nullable(),
    })
    .nullable(),
  deletedConversation: z.boolean(),
  artefactVersion: z.int().min(1),
  model: z.string().nullable(),
});
export type DocumentProvenance = z.infer<typeof documentProvenanceSchema>;

export const projectDocumentSchema = z.object({
  id: documentIdSchema,
  projectId: projectIdSchema,
  title: z.string(),
  mediaType: z.string(),
  sizeBytes: z.number().int().min(0),
  version: z.int().min(1),
  /**
   * Moves with every change to its readers, classification or origin: the value to send as
   * `expectedVersion` when changing them. Absent from older releases.
   */
  metadataVersion: z.int().min(1).optional(),
  status: documentStatusSchema,
  failureReason: z.string().nullable(),
  /** A level id of the tenant scheme. */
  classification: z.string(),
  origin: storedDocumentOriginSchema,
  readers: z.array(storedReaderSchema),
  uploadedBy: userIdSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  /** Its folder (knowledge-folders); null or absent: the root of its section. */
  folderId: folderIdSchema.nullable().optional(),
  /** Absent from older releases (N-1 strips it); clients treat an unknown kind as `file`. */
  kind: z.string().optional(),
  /** Set on documents of kind `artefact` (design §5). */
  provenance: documentProvenanceSchema.nullable().optional(),
});
export type ProjectDocument = z.infer<typeof projectDocumentSchema>;

/**
 * The check compares a statement with the passage text: for an OCR passage, that is the
 * transcription, never the original. A flagged one is never more than `unchecked` (clients show a
 * supported OCR citation as "supported by the transcription").
 */
export function ocrCitationStatus(
  status: CitationStatus,
  provenance: PassageProvenance | null | undefined,
): CitationStatus {
  return provenance?.flagged && status === "supported" ? "unchecked" : status;
}
